import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Grid, Loader } from "semantic-ui-react";
import BCDashboard from "../ui/BCDashboard";
import BCLeaderboard from "../ui/BCLeaderboard";
import { BCColorArray } from "../ui/BCColorArray";
import BCPieChart from "../ui/BCPieChart";
import BCLegend from "../ui/BCLegend";
import BCMessage from "../ui/BCMessage";
import { getDashboard, getW9Url } from "../../api/BrokerApi";
import { getMockDashboard, getMockW9Url } from "../../api/mock/MockBrokerApi";
import { calculateArrTotal, convertToDollar, firstToUpper, guid, isValidDashboard, isValidPermission, handleErrorResponse } from "../../Helpers";
import BCEmptyState from "../ui/BCEmptyState";
import BCButton from "../ui/BCButton";
import "../../styles/dashboard.scss";
import "../../styles/calendar.scss";
import "../../styles/main.scss";
import Bar from "../ui/BCBarGraph";
import * as d3 from "d3";
import { css } from "@emotion/css";
import { FeatureFlags } from "../constants/LocalServerConstants";
import { getIsWhiteLabeled, whoAmI } from "../../helpers/PermissionHelper";
import { Personas } from "../constants/GlobalConstants";

const BPDashboard = ({ 
  isGreaterThanMobile, 
  isGreaterThanTablet, 
  isSidebar, 
  isMessage, 
  setIsMessage, 
  errorResponse, 
  setErrorResponse,
  proxyUser, 
  permissions, 
  brokerColor, 
  isW9MessageVisible, 
  setIsW9MessageVisible, 
  user 
}) => {
  const [dashboardData, setDashboardData] = useState(undefined as any);
  const [isLoading, setIsLoading] = useState(true);
  const persona = whoAmI(user, proxyUser);
  const isAdminBrmLoBrokerRetailGuarantor = [
    Personas.SYSTEM_ADMIN,
    Personas.SALES_ADMIN,
    Personas.BROKER_ADMIN,
    Personas.LOAN_OFFICER,
    Personas.BRM,
    Personas.NWL_GUARANTOR,
    Personas.BROKER
  ].indexOf(persona) > -1;

  const colorArray = [
    "#01b8aa",
    "#28383c",
    "#fd625e",
    "#f2c80f",
    "#5f6b6d",
    "#8ad4eb"
  ];

  const dealStatusLegendArray = [
    {
      value: "value",
      format: "dollar"
    },
    {
      value: "in",
      format: "text"
    },
    {
      value: "name",
      format: "default"
    }
  ];

  const topBorrowersArray = [
    {
      value: "borrowerName",
      format: "default"
    },
    {
      value: "amountInCommitment",
      format: "dollar"
    }
  ];

  const brokerIconOverrideCSS = css`
  color:${brokerColor ? brokerColor : "black"} !important;
  font-size: 4em !important;
  `;

  const history = useHistory();
  const handleError = useCallback(() => {
    history.push("/error");
  }, [history]);

  const handleW9URLAsync = async () => {
    try {
      const response = FeatureFlags.IS_MOCK ? await getMockW9Url(user.id) : await getW9Url(proxyUser.id, window.location.toString()) as any;
      if (response && response.ok) {
        const getW9UrlObj = await response.json();
        if (getW9UrlObj) {
          const win = window.location.replace(getW9UrlObj) as any;
          win.focus();
          setIsW9MessageVisible(true);
        } 
      }
      else {
        await handleErrorResponse(response, setIsMessage, setErrorResponse);
      }
    }
    catch (error) {
      setIsMessage(false);
    }
  };
 
  const convertClosingMonthsToArray = (closingMonths) => {
    return d3.range(dashboardData && dashboardData.closingMonths.length).map((item, index) => ({
      index: index,
      date: closingMonths[index].monthName,
      value: closingMonths[index].closingAmount
    }));
  };

  const handleGMR = useCallback(() => {
    history.push(`/getmyrate/${guid()}`);
  }, [history]);
  
  const handleApplication = useCallback(() => {
    history.push(`/application/borrower/${guid()}`);
  }, [history]);

  const convertDealStatusToArray = (dealStatus) => {
    const dealStatusArray = [] as any;
    for (const [key, value] of Object.entries(dealStatus)) {
      dealStatusArray.push({
        name: firstToUpper(key),
        value: value
      });
    }
    return dealStatusArray;
  };
 
  useEffect(() => {
    const getLODashboardAsync = async () => {
      try {
        const response = FeatureFlags.IS_MOCK ? await getMockDashboard() : await getDashboard(proxyUser.id);
        if (response && response.ok) {
          setIsMessage(false);
          setDashboardData(await response.json());
        }
        else {
          await handleErrorResponse(response, setIsMessage, setErrorResponse);
        }
      }
      catch (error) {
        setIsMessage(false);
        handleError();
      }
      setIsLoading(false);
    };

    getLODashboardAsync();
  }, [setIsMessage, setErrorResponse, handleError, proxyUser]);
 
  const projectTypeLegendArray = [
    {
      value: "projectTypeName",
      format: "default"
    },
    {
      value: "projectTypeTotal",
      format: "dollar"
    }
  ];

  const MetaTotal = ({ ...props }) => {
    if (props.data) {
      return (
        <div className="dashboard-meta-total">
          
          {convertToDollar(calculateArrTotal(props.data, props.label))}
        </div>
      );
    }
    else {
      return null;
    }
  };

  const BPDashboardLayout = [
    [
      {
        title: "Closings By Month",
        size: 10,
        content: <React.Fragment>
          {isValidDashboard(dashboardData, "closingMonths", "monthNumber") ?
           <Bar
            data={dashboardData && convertClosingMonthsToArray(dashboardData.closingMonths)}
            width={700}
            height={400}
            top={20}
            bottom={20}
            left={100}
            right={50}
            graphColors={BCColorArray}
            yLabel={"Closing Amount (Millions)"}
          />:
          <BCEmptyState 
            className={getIsWhiteLabeled() ? brokerIconOverrideCSS : void 0}
            text={"No deals to display, start pricing a loan today!"} 
            icon={"chart bar"} 
            brokerColor={brokerColor}
            button={isAdminBrmLoBrokerRetailGuarantor && <BCButton labelPosition="left" iconName="add" brokerColor={brokerColor} handleOnClick={handleGMR}><div>Start Pricing</div></BCButton>}
            />}
        </React.Fragment> 
      },
      {
        title: "Deal Status",
        size: 6,
        meta: <React.Fragment>
          {isValidDashboard(dashboardData, "dealStatus", undefined, true) && <MetaTotal data={dashboardData && convertDealStatusToArray(dashboardData.dealStatus)} label="value" />}</React.Fragment>, 
         content: <React.Fragment>
          {isValidDashboard(dashboardData, "dealStatus", undefined, true)?
            <BCLegend
              data={dashboardData && convertDealStatusToArray(dashboardData.dealStatus)}
              labels={dealStatusLegendArray}
              colorArray={colorArray}
              shape="circle"
              spacing
            /> :
            <BCEmptyState 
              className={getIsWhiteLabeled() ? brokerIconOverrideCSS : void 0}
              text={"No deals to display, start pricing a loan today!"} 
              icon={"chart line"} 
              brokerColor={brokerColor}
              button={isAdminBrmLoBrokerRetailGuarantor && <BCButton labelPosition="left" iconName="add" handleOnClick={handleGMR}><div>Start Pricing</div></BCButton>}
              />
              
          }</React.Fragment>
      }
    ],
    [
      {
        title: "Top Borrowers",
        size: 6,
          content: <React.Fragment>
            {isValidDashboard(dashboardData, "topBorrowers", "borrowerName")? 
            <BCLeaderboard
              data={dashboardData && dashboardData.topBorrowers}
              labels={topBorrowersArray}
              colorArray={colorArray}
              />: 
              <BCEmptyState 
                className={getIsWhiteLabeled() ? brokerIconOverrideCSS : void 0}
                text={"No Borrowers yet, start a Loan Application today!"} 
                icon={"address book outline"}
                brokerColor={brokerColor}
                button={isAdminBrmLoBrokerRetailGuarantor && <BCButton labelPosition="left" iconName="add" brokerColor={brokerColor} handleOnClick={handleApplication}><div>Start Application</div></BCButton>}/>
        }</React.Fragment>         
      },
      {
        title: "Portfolio By Product Type",
        size: 10,
        content: <React.Fragment>
          {isValidDashboard(dashboardData, "projectTypes", "projectTypeName")?
            <Grid>
              <Grid.Row>
                <Grid.Column width={8}>
                  <BCPieChart
                    data={dashboardData && dashboardData.projectTypes.filter(x => x.projectTypeTotal !== null)}
                    valueName="projectTypeTotal"
                    addedWidth={0}
                    width={400}
                    height={400}
                    innerRadius={75}
                    outerRadius={200}
                    colorArray={BCColorArray}
                  />
                </Grid.Column>
                <Grid.Column width={8}>
                  <div className="vertical-center">
                    <BCLegend
                      data={dashboardData && dashboardData.projectTypes.filter(x => x.projectTypeTotal !== null)}
                      labels={projectTypeLegendArray}
                      colorArray={BCColorArray}
                      shape="circle"
                    />
                  </div>
                </Grid.Column>
              </Grid.Row>
            </Grid> : <BCEmptyState 
              className={getIsWhiteLabeled() ? brokerIconOverrideCSS : void 0}
              text={"No deals to display, start pricing a loan today!"} 
              icon={"chart pie"}
              brokerColor={brokerColor}
              button={isAdminBrmLoBrokerRetailGuarantor && <BCButton labelPosition="left" iconName="add" brokerColor={brokerColor} handleOnClick={handleGMR}><div>Start Pricing</div></BCButton>}
            />
      }</React.Fragment>
      }
    ]
  ];

  return (
    <div className={(isGreaterThanTablet && isSidebar) ? "hide-sidebar" : "sidebar"}>
      {isLoading ? <Loader className="" active inline="centered" size={"big"} /> : <div>
        <BCMessage
          animation={"fly down"}
          duration={750}
          visible={isMessage}
          transitionOnMount
          styles="full-width"
          onDismiss={() => setIsMessage(false)}
          errorResponse={errorResponse}
        />
         {!isW9MessageVisible && <div className={"primary-message"}>
          {isValidPermission(permissions, "BrokerW9", "read") && <BCMessage
            animation={"fly down"}
            duration={10}
            visible={!isW9MessageVisible}
            transitionOnMount
            styles="full-width"
            negative
            color="blue"
            header={"Reminder!"}
            onDismiss={() => setIsW9MessageVisible(!isW9MessageVisible)}
            clickable={() => handleW9URLAsync()}
            text={"In order to collect your broker fee, you must complete your W9 prior to receiving payment. Please click here or navigate to your profile in the top left to complete it."}
          />}
          </div>}
        {isAdminBrmLoBrokerRetailGuarantor && <BCDashboard
          DashboardLayout={BPDashboardLayout}
          isGreaterThanMobile={isGreaterThanMobile}
          brokerColor={brokerColor}
        />}
      </div>}
    </div>
  );
};

export default BPDashboard;